import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Plus, Edit, Trash2, Search, X, Briefcase, GripVertical } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminServices = () => {
  const { token } = useAuth();
  const { refreshStats } = useOutletContext();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    number: '01',
    title: '',
    title_es: '',
    tagline: '',
    tagline_es: '',
    description: '',
    description_es: '',
    deliverables: '',
    deliverables_es: '',
    image_url: '',
    cta_text: 'Get Started',
    cta_text_es: 'Comenzar',
    cta_link: '/contact',
    display_order: 0,
    is_dark: false,
    active: true
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/services`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setServices(data);
      }
    } catch (error) {
      toast.error('Error loading services');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const submitData = {
      ...formData,
      display_order: parseInt(formData.display_order) || 0,
      deliverables: formData.deliverables.split('\n').filter(item => item.trim()),
      deliverables_es: formData.deliverables_es ? formData.deliverables_es.split('\n').filter(item => item.trim()) : null
    };

    try {
      const url = editingService 
        ? `${API_URL}/api/admin/services/${editingService.id}`
        : `${API_URL}/api/admin/services`;
      
      const response = await fetch(url, {
        method: editingService ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        toast.success(editingService ? 'Service updated!' : 'Service created!');
        setShowModal(false);
        resetForm();
        fetchServices();
        refreshStats?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error saving service');
      }
    } catch (error) {
      toast.error('Error saving service');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/services/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Service deleted!');
        fetchServices();
        refreshStats?.();
      } else {
        toast.error('Error deleting service');
      }
    } catch (error) {
      toast.error('Error deleting service');
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      number: service.number || '01',
      title: service.title || '',
      title_es: service.title_es || '',
      tagline: service.tagline || '',
      tagline_es: service.tagline_es || '',
      description: service.description || '',
      description_es: service.description_es || '',
      deliverables: (service.deliverables || []).join('\n'),
      deliverables_es: (service.deliverables_es || []).join('\n'),
      image_url: service.image_url || '',
      cta_text: service.cta_text || 'Get Started',
      cta_text_es: service.cta_text_es || 'Comenzar',
      cta_link: service.cta_link || '/contact',
      display_order: service.display_order || 0,
      is_dark: service.is_dark || false,
      active: service.active !== false
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingService(null);
    setFormData({
      number: String(services.length + 1).padStart(2, '0'),
      title: '',
      title_es: '',
      tagline: '',
      tagline_es: '',
      description: '',
      description_es: '',
      deliverables: '',
      deliverables_es: '',
      image_url: '',
      cta_text: 'Get Started',
      cta_text_es: 'Comenzar',
      cta_link: '/contact',
      display_order: services.length,
      is_dark: services.length % 2 === 1,
      active: true
    });
  };

  const openNewModal = () => {
    resetForm();
    setShowModal(true);
  };

  const filteredServices = services.filter(service =>
    service.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    service.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-terracota"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-services">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Services</h1>
          <p className="text-stone-500">Manage services displayed on the Evoke page</p>
        </div>
        <button
          onClick={openNewModal}
          className="inline-flex items-center gap-2 bg-terracota text-white px-4 py-2 rounded-lg hover:bg-stone-900 transition-colors"
          data-testid="add-service-btn"
        >
          <Plus className="w-5 h-5" />
          Add Service
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-stone-400" />
        <input
          type="text"
          placeholder="Search services..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracota/50"
        />
      </div>

      {/* Services List */}
      {filteredServices.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-stone-200">
          <Briefcase className="w-12 h-12 text-stone-300 mx-auto mb-4" />
          <p className="text-stone-500">No services yet. Create your first service!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredServices.map((service) => (
            <div
              key={service.id}
              className={`p-6 rounded-xl border ${service.is_dark ? 'bg-stone-900 text-white border-stone-700' : 'bg-white border-stone-200'}`}
              data-testid={`service-${service.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="flex items-center gap-2">
                    <GripVertical className={`w-5 h-5 ${service.is_dark ? 'text-stone-500' : 'text-stone-400'} cursor-grab`} />
                    <span className={`text-3xl font-serif font-bold ${service.is_dark ? 'text-terracota' : 'text-terracota'}`}>
                      {service.number}
                    </span>
                  </div>
                  <div>
                    <h3 className={`text-xl font-semibold ${service.is_dark ? 'text-white' : 'text-stone-900'}`}>
                      {service.title}
                    </h3>
                    {service.tagline && (
                      <p className={`italic ${service.is_dark ? 'text-stone-400' : 'text-stone-500'}`}>
                        {service.tagline}
                      </p>
                    )}
                    <p className={`mt-2 text-sm ${service.is_dark ? 'text-stone-400' : 'text-stone-600'}`}>
                      {service.description?.substring(0, 120)}...
                    </p>
                    {service.deliverables?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {service.deliverables.slice(0, 3).map((item, idx) => (
                          <span 
                            key={idx} 
                            className={`text-xs px-2 py-1 rounded-full ${
                              service.is_dark ? 'bg-stone-800 text-stone-300' : 'bg-stone-100 text-stone-600'
                            }`}
                          >
                            {item}
                          </span>
                        ))}
                        {service.deliverables.length > 3 && (
                          <span className={`text-xs ${service.is_dark ? 'text-stone-500' : 'text-stone-400'}`}>
                            +{service.deliverables.length - 3} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    service.active 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-stone-100 text-stone-500'
                  }`}>
                    {service.active ? 'Active' : 'Hidden'}
                  </span>
                  <button
                    onClick={() => handleEdit(service)}
                    className={`p-2 rounded-lg hover:bg-opacity-80 transition-colors ${
                      service.is_dark ? 'hover:bg-stone-800 text-stone-400' : 'hover:bg-stone-100 text-stone-500'
                    }`}
                    data-testid={`edit-service-${service.id}`}
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(service.id)}
                    className={`p-2 rounded-lg hover:bg-red-50 text-red-500 transition-colors ${
                      service.is_dark ? 'hover:bg-red-900/20' : ''
                    }`}
                    data-testid={`delete-service-${service.id}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-8">
          <div className="bg-white rounded-xl w-full max-w-2xl mx-4 shadow-2xl">
            <div className="p-6 border-b border-stone-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-stone-900">
                {editingService ? 'Edit Service' : 'New Service'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Number & Display Order */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Number *
                  </label>
                  <input
                    type="text"
                    value={formData.number}
                    onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracota/50"
                    placeholder="01"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracota/50"
                  />
                </div>
                <div className="flex items-end gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_dark}
                      onChange={(e) => setFormData({ ...formData, is_dark: e.target.checked })}
                      className="w-4 h-4 text-terracota rounded"
                    />
                    <span className="text-sm text-stone-700">Dark Section</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      className="w-4 h-4 text-terracota rounded"
                    />
                    <span className="text-sm text-stone-700">Active</span>
                  </label>
                </div>
              </div>

              {/* Title EN/ES */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Title (EN) *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracota/50"
                    placeholder="Brand Identity"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Title (ES)
                  </label>
                  <input
                    type="text"
                    value={formData.title_es}
                    onChange={(e) => setFormData({ ...formData, title_es: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracota/50"
                    placeholder="Identidad de Marca"
                  />
                </div>
              </div>

              {/* Tagline EN/ES */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Tagline (EN)
                  </label>
                  <input
                    type="text"
                    value={formData.tagline}
                    onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracota/50"
                    placeholder="A visual system that feels inevitable"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Tagline (ES)
                  </label>
                  <input
                    type="text"
                    value={formData.tagline_es}
                    onChange={(e) => setFormData({ ...formData, tagline_es: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracota/50"
                    placeholder="Un sistema visual que se siente inevitable"
                  />
                </div>
              </div>

              {/* Description EN */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Description (EN) *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracota/50"
                  rows={3}
                  placeholder="Full visual identity system — type, color, voice, visuals — that feel like they were destined to exist."
                  required
                />
              </div>

              {/* Description ES */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Description (ES)
                </label>
                <textarea
                  value={formData.description_es}
                  onChange={(e) => setFormData({ ...formData, description_es: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracota/50"
                  rows={3}
                  placeholder="Sistema de identidad visual completo..."
                />
              </div>

              {/* Deliverables EN */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Deliverables (EN) - One per line
                </label>
                <textarea
                  value={formData.deliverables}
                  onChange={(e) => setFormData({ ...formData, deliverables: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracota/50"
                  rows={4}
                  placeholder="Logo & Visual System&#10;Brand Guidelines&#10;Typography Suite&#10;Color Palette&#10;Voice & Tone Guide"
                />
              </div>

              {/* Deliverables ES */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Deliverables (ES) - One per line
                </label>
                <textarea
                  value={formData.deliverables_es}
                  onChange={(e) => setFormData({ ...formData, deliverables_es: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracota/50"
                  rows={4}
                  placeholder="Logo y Sistema Visual&#10;Guía de Marca&#10;Suite Tipográfica"
                />
              </div>

              {/* Image URL */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Image URL
                </label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracota/50"
                  placeholder="https://..."
                />
              </div>

              {/* CTA */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    CTA Text (EN)
                  </label>
                  <input
                    type="text"
                    value={formData.cta_text}
                    onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracota/50"
                    placeholder="Get Started"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    CTA Text (ES)
                  </label>
                  <input
                    type="text"
                    value={formData.cta_text_es}
                    onChange={(e) => setFormData({ ...formData, cta_text_es: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracota/50"
                    placeholder="Comenzar"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    CTA Link
                  </label>
                  <input
                    type="text"
                    value={formData.cta_link}
                    onChange={(e) => setFormData({ ...formData, cta_link: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracota/50"
                    placeholder="/contact"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-stone-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-stone-300 rounded-lg text-stone-700 hover:bg-stone-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-terracota text-white rounded-lg hover:bg-stone-900 transition-colors"
                  data-testid="save-service-btn"
                >
                  {editingService ? 'Update Service' : 'Create Service'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminServices;
